'use client';

import { UserCheck, UserPlus } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { PUBLIC_API_URL } from '@/lib/public-api-url';

interface RelationshipState {
  targetUserId: number;
  isFollowing: boolean;
  followsYou: boolean;
  isFriend: boolean;
  isBlocked?: boolean;
  blockedByMe?: boolean;
  blockedByTarget?: boolean;
}

interface PendingBlockProjection {
  blockedByMe?: boolean;
  blockedByTarget?: boolean;
}

export function FollowUserButton({
  userId,
  compact = false,
}: {
  userId: number | string;
  compact?: boolean;
}) {
  const { data: session, status } = useSession();
  const accessToken = session?.accessToken;
  const currentUserId = session?.user.id;
  const targetUserId = Number(userId);
  const [relationship, setRelationship] = useState<RelationshipState | null>(null);
  const [isRelationshipLoading, setIsRelationshipLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const pendingBlockRef = useRef<PendingBlockProjection | null>(null);

  const applyPendingBlock = useCallback((state: RelationshipState): RelationshipState => {
    const pending = pendingBlockRef.current;
    if (!pending) return state;
    const blockedByMe = pending.blockedByMe ?? state.blockedByMe ?? false;
    const blockedByTarget = pending.blockedByTarget ?? state.blockedByTarget ?? false;
    if (
      (pending.blockedByMe === undefined || pending.blockedByMe === state.blockedByMe) &&
      (pending.blockedByTarget === undefined || pending.blockedByTarget === state.blockedByTarget)
    ) {
      pendingBlockRef.current = null;
      return state;
    }
    const isBlocked = blockedByMe || blockedByTarget;
    return {
      ...state,
      blockedByMe,
      blockedByTarget,
      isBlocked,
      isFollowing: isBlocked ? false : state.isFollowing,
      followsYou: isBlocked ? false : state.followsYou,
      isFriend: isBlocked ? false : state.isFriend,
    };
  }, []);

  const loadRelationship = useCallback(async () => {
    if (!accessToken || !Number.isSafeInteger(targetUserId)) return;
    setIsRelationshipLoading(true);
    try {
      const response = await fetch(`${PUBLIC_API_URL}/api/users/${targetUserId}/relationship`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(String(response.status));
      setRelationship(applyPendingBlock((await response.json()) as RelationshipState));
    } catch {
      // POST subscribe is idempotent, so a transient relationship-read failure must not
      // leave the primary action permanently disabled. Preserve an already known state;
      // with no state the button safely falls back to POST and adopts its response.
    } finally {
      setIsRelationshipLoading(false);
    }
  }, [accessToken, applyPendingBlock, targetUserId]);

  useEffect(() => {
    const loadTimer = window.setTimeout(() => {
      setRelationship(null);
      void loadRelationship();
    }, 0);
    return () => window.clearTimeout(loadTimer);
  }, [loadRelationship]);

  useEffect(() => {
    const retryTimers: Array<ReturnType<typeof setTimeout>> = [];
    const handleRelationshipUpdate = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const source =
        detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : null;
      const blockerId = Number(source?.blockerId);
      const blockedId = Number(source?.blockedId);
      const blocked = source?.blocked;
      const isRelevantPair =
        typeof blocked === 'boolean' &&
        ((blockerId === currentUserId && blockedId === targetUserId) ||
          (blockerId === targetUserId && blockedId === currentUserId));
      if (!isRelevantPair) {
        void loadRelationship();
        return;
      }

      const previous = pendingBlockRef.current;
      pendingBlockRef.current = {
        blockedByMe: blockerId === currentUserId ? blocked : previous?.blockedByMe,
        blockedByTarget: blockerId === targetUserId ? blocked : previous?.blockedByTarget,
      };
      setRelationship((current) => (current ? applyPendingBlock(current) : current));
      for (const delay of [6_000, 15_000, 30_000, 60_000, 120_000, 300_000]) {
        retryTimers.push(setTimeout(() => void loadRelationship(), delay));
      }
    };
    window.addEventListener('social-relationship-updated', handleRelationshipUpdate);
    return () => {
      window.removeEventListener('social-relationship-updated', handleRelationshipUpdate);
      for (const timer of retryTimers) clearTimeout(timer);
    };
  }, [applyPendingBlock, currentUserId, loadRelationship, targetUserId]);

  if (
    status !== 'authenticated' ||
    !accessToken ||
    currentUserId === targetUserId ||
    !Number.isSafeInteger(targetUserId)
  ) {
    return null;
  }

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${PUBLIC_API_URL}/api/users/${targetUserId}/subscribe`, {
        method: relationship?.isFollowing ? 'DELETE' : 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error(String(response.status));
      setRelationship((await response.json()) as RelationshipState);
    } catch {
      toast.error('Не удалось изменить подписку. Попробуйте ещё раз.');
    } finally {
      setIsLoading(false);
    }
  };

  const label = relationship?.isFriend
    ? 'В друзьях'
    : relationship?.isFollowing
      ? 'Вы подписаны'
      : relationship?.followsYou
        ? 'Добавить в друзья'
        : 'Подписаться';

  const mobileLabel = relationship?.isFriend
    ? 'В друзьях'
    : relationship?.isFollowing
      ? 'Вы подписаны'
      : relationship?.followsYou
        ? 'В друзья'
        : 'Подписаться';

  const isBlocked = Boolean(
    relationship?.isBlocked || relationship?.blockedByMe || relationship?.blockedByTarget,
  );

  return (
    <Button
      type="button"
      size={compact ? 'sm' : 'default'}
      variant={relationship?.isFollowing ? 'outline' : 'default'}
      disabled={isLoading || isRelationshipLoading || isBlocked}
      onClick={handleClick}
      className={
        compact
          ? 'h-8 whitespace-nowrap px-3 text-xs'
          : 'whitespace-nowrap max-md:h-8 max-md:px-3 max-md:text-xs'
      }
      title={
        isBlocked
          ? 'Подписка недоступна из-за блокировки'
          : relationship?.isFollowing
            ? 'Нажмите, чтобы отписаться'
            : undefined
      }
    >
      {relationship?.isFollowing ? (
        <UserCheck className="mr-1.5 h-4 w-4" />
      ) : (
        <UserPlus className="mr-1.5 h-4 w-4" />
      )}
      {isRelationshipLoading ? (
        'Загружаем…'
      ) : isLoading ? (
        'Сохраняем…'
      ) : isBlocked ? (
        'Подписка недоступна'
      ) : compact ? (
        label
      ) : (
        <>
          <span className="md:hidden">{mobileLabel}</span>
          <span className="hidden md:inline">{label}</span>
        </>
      )}
    </Button>
  );
}
